'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Four equal category tiles: image, name and a one-line caption, the whole
 * tile a single link.
 *
 * <p>Two columns on a phone, four from 769 px. The tile's link wraps the image
 * and the text, and the image has an empty alt when the tile title already
 * names it, so a screen reader hears each category once.
 */
export interface SpatialKitchenCategoryTile {
  title?: Localized;
  caption?: Localized;
  href?: string;
  image?: SkImageRef;
  /**
   * A from-price, as display text rather than a number — "From £59" — because
   * the entry price for a category is a merchandising decision, not the minimum
   * of whatever happens to be in the catalogue this week.
   *
   * <p>It is the single most useful thing a category tile can carry. A shopper
   * deciding whether this shop is for them answers "how much?" before anything
   * else, and a tile that will not say is read as expensive.
   */
  price?: Localized;
  /** Lead time for the category, e.g. "3–4 weeks". The second question. */
  lead?: Localized;
}

export interface SpatialKitchenCategoryTilesProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  tiles?: SpatialKitchenCategoryTile[];
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-tiles__grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem}
@media (max-width:420px){.sk-tiles__grid{grid-template-columns:minmax(0,1fr)}}
/* auto-fit, not a fixed four: the block takes up to four tiles but is often
   given three, and a fixed four-column track leaves a visible hole in the row. */
@media (min-width:769px){.sk-tiles__grid{grid-template-columns:repeat(auto-fit,minmax(min(100%,14rem),1fr));gap:clamp(1rem,1.75vw,1.75rem)}}
.sk-tile a{display:grid;gap:.875rem;color:inherit;text-decoration:none}
.sk-tile__media{aspect-ratio:3/4;border-radius:8px}
.sk-tile__media img{transition:transform .5s ease}
.sk-tile a:hover .sk-tile__media img{transform:scale(1.03)}
.sk-tile__title{display:flex;align-items:center;justify-content:space-between;gap:.5rem;font-size:clamp(1.125rem,1rem + .4vw,1.375rem)}
.sk-tile a:hover .sk-tile__title{text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:.3em}
.sk-tile__caption{font-size:.9375rem;color:var(--sk-muted);margin:-.375rem 0 0}
/* Price and lead time on one row, separated by a rule rather than a bullet so
   the two facts stay legible when the tile is half a phone wide. */
.sk-tile__facts{display:flex;flex-wrap:wrap;align-items:baseline;gap:.25rem .625rem;margin:-.25rem 0 0;font-size:.9375rem}
.sk-tile__price{font-weight:600;color:var(--sk-ink)}
.sk-tile__lead{color:var(--sk-muted)}
.sk-tile__lead::before{content:'';display:inline-block;width:1px;height:.8em;margin-right:.625rem;background:currentColor;opacity:.4;vertical-align:baseline}
.sk-tile__facts .sk-tile__lead:first-child::before{display:none}
`;

export function SpatialKitchenCategoryTiles(props: SpatialKitchenCategoryTilesProps) {
  const tiles = items<SpatialKitchenCategoryTile>(props.tiles).slice(0, 4);
  const title = text(props.title);
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const TileTitle = title ? childLevel(level) : level;
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-tiles sk-section ${surfaceClass(props.surface, 'white')}`}
      {...(title ? { 'aria-labelledby': titleId } : {})}
    >
      <SkBaseStyles />
      <SkStyles id="category-tiles" css={CSS} />
      <div className="sk-container">
        {title && (
          <div className="sk-section-head">
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <Title id={titleId} className="sk-heading sk-h2">
              {title}
            </Title>
            {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
          </div>
        )}
        <ul className="sk-tiles__grid sk-list-reset">
          {tiles.map((tile, index) => {
            const tileTitle = text(tile.title);
            const caption = text(tile.caption);
            const price = text(tile.price);
            const lead = text(tile.lead);
            return (
              <li key={`${tileTitle}-${index}`} className="sk-tile">
                <SkLink href={tile.href}>
                  <div className="sk-tile__media sk-media">
                    <SkImage
                      image={tile.image ? { ...tile.image, alt: '' } : undefined}
                      sizes="(min-width: 769px) 25vw, 50vw"
                    />
                  </div>
                  <TileTitle className="sk-heading sk-tile__title">
                    {tileTitle}
                    <Icon name="arrow-right" size={18} />
                  </TileTitle>
                  {caption && <p className="sk-tile__caption">{caption}</p>}
                  {(price || lead) && (
                    <p className="sk-tile__facts">
                      {price && <span className="sk-tile__price">{price}</span>}
                      {lead && <span className="sk-tile__lead">{lead}</span>}
                    </p>
                  )}
                </SkLink>
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
